import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { ExecutionDetail } from '../../types/agent-studio';
import {
  AgentStatusPill,
  TraceLogExpandable,
} from '../../components/agent-studio';

// ── Helpers ───────────────────────────────────────────────────────────────────

const INVOCATION_LABELS: Record<string, string> = {
  pipeline:        'Пайплайн',
  studio_test:     'Студия',
  task:            'Задача',
  eval_harness:    'Оценка',
  workspace_query: 'Рабочее пространство',
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={{ marginTop: '20px' }}>
      <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ height: '20px', background: 'var(--bg-subtle, rgba(0,0,0,0.04))', borderRadius: '4px', marginBottom: '12px', width: '40%' }} />
        <div style={{ height: '60px', background: 'var(--bg-subtle, rgba(0,0,0,0.04))', borderRadius: '4px' }} />
      </div>
      <div className="card" style={{ padding: '20px' }}>
        <div style={{ height: '200px', background: 'var(--bg-subtle, rgba(0,0,0,0.04))', borderRadius: '4px' }} />
      </div>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="card" style={{ padding: '20px', marginTop: '20px', borderLeft: '3px solid var(--red, #C0392B)', background: 'rgba(198, 40, 40, 0.04)' }}>
      <div className="t-eyebrow" style={{ marginBottom: '8px', color: 'var(--red, #C0392B)' }}>
        ОШИБКА ЗАГРУЗКИ
      </div>
      <div style={{ fontSize: '14px', color: 'var(--red, #C0392B)' }}>{message}</div>
    </div>
  );
}

function MetricItem({ label, value, isMonospace }: {
  label: string;
  value: string;
  isMonospace?: boolean;
}) {
  return (
    <div>
      <div className="t-eyebrow" style={{ fontSize: '11px', marginBottom: '4px', color: 'var(--ink-secondary)' }}>
        {label}
      </div>
      <div style={{
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: isMonospace ? "'Menlo', 'Consolas', 'Courier New', monospace" : 'inherit',
      }}>
        {value}
      </div>
    </div>
  );
}

function JsonSection({ title, data }: {
  title: string;
  data: Record<string, unknown>;
}) {
  const [copied, setCopied] = useState(false);
  const jsonString = JSON.stringify(data, null, 2);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard failures
    }
  }

  return (
    <div className="card" style={{ marginBottom: '16px', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div className="t-eyebrow">{title}</div>
        <button
          onClick={handleCopy}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
            padding: '4px 12px',
            fontSize: '12px',
            cursor: 'pointer',
            borderRadius: '4px',
            color: 'var(--ink-secondary)',
          }}
        >
          {copied ? 'Скопировано ✓' : 'Скопировать'}
        </button>
      </div>
      <pre style={{
        background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
        padding: '16px',
        borderRadius: '4px',
        fontFamily: "'Menlo', 'Consolas', 'Courier New', monospace",
        fontSize: '13px',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '500px',
        overflow: 'auto',
        margin: 0,
        color: 'var(--ink-primary)',
      }}>
        {jsonString}
      </pre>
    </div>
  );
}

function DetailView({ execution, systemName, navigate }: {
  execution: ExecutionDetail;
  systemName: string;
  navigate: (path: string) => void;
}) {
  return (
    <>
      {/* Header */}
      <div style={{ marginTop: '12px', marginBottom: '8px' }}>
        <h1 className="t-display" style={{ margin: 0 }}>
          Запуск #{execution.id.slice(-8)}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
          <AgentStatusPill status={execution.status} />
          <span style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--ink-secondary)' }}>
            {execution.id}
          </span>
        </div>
      </div>

      {/* Back button */}
      <div style={{ textAlign: 'right', marginTop: '-32px', marginBottom: '20px' }}>
        <button
          onClick={() => navigate(`/agents/${systemName}#executions`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--navy)',
            fontSize: '13px',
            cursor: 'pointer',
            padding: '4px 8px',
          }}
        >
          ← К списку запусков
        </button>
      </div>

      {/* Metrics card */}
      <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '16px' }}>МЕТРИКИ</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '20px' }}>
          <MetricItem
            label="Латентность"
            value={execution.latency_seconds != null ? `${execution.latency_seconds.toFixed(1)}с` : '—'}
          />
          <MetricItem
            label="Стоимость"
            value={execution.cost_usd != null ? `$${execution.cost_usd.toFixed(4)}` : '—'}
          />
          <MetricItem
            label="Входящих токенов"
            value={execution.input_tokens != null ? execution.input_tokens.toLocaleString('ru-RU') : '—'}
          />
          <MetricItem
            label="Исходящих токенов"
            value={execution.output_tokens != null ? execution.output_tokens.toLocaleString('ru-RU') : '—'}
          />
          <MetricItem
            label="Источник"
            value={INVOCATION_LABELS[execution.invocation_source] || execution.invocation_source}
          />
          <MetricItem
            label="Версия агента"
            value={execution.agent_version_id ? execution.agent_version_id.slice(-8) : '—'}
            isMonospace={true}
          />
        </div>
        <div style={{
          display: 'flex',
          gap: '24px',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
          fontSize: '13px',
          color: 'var(--ink-secondary)',
          flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ marginRight: '6px' }}>Начало:</span>
            {formatTimestamp(execution.started_at)}
          </div>
          {execution.completed_at && (
            <div>
              <span style={{ marginRight: '6px' }}>Завершение:</span>
              {formatTimestamp(execution.completed_at)}
            </div>
          )}
          {execution.pipeline_run_id && (
            <div>
              <span style={{ marginRight: '6px' }}>Pipeline run:</span>
              <span style={{ fontFamily: 'monospace' }}>{execution.pipeline_run_id.slice(-8)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Error message card (only when failed) */}
      {execution.status === 'failed' && execution.error_message && (
        <div className="card" style={{ padding: '20px', marginBottom: '16px', borderLeft: '3px solid var(--red, #C0392B)', background: 'rgba(198, 40, 40, 0.04)' }}>
          <div className="t-eyebrow" style={{ marginBottom: '8px', color: 'var(--red, #C0392B)' }}>
            ОШИБКА
          </div>
          <pre style={{
            margin: 0,
            fontFamily: "'Menlo', 'Consolas', 'Courier New', monospace",
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--red, #C0392B)',
          }}>
            {execution.error_message}
          </pre>
        </div>
      )}

      {/* Trace log */}
      <div style={{ marginBottom: '16px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '12px' }}>ХОД ВЫПОЛНЕНИЯ</div>
        <TraceLogExpandable execution={execution} defaultExpanded={true} />
      </div>

      {/* Input context (Phase 2: populated when execution_logs stores context) */}
      {Object.keys(execution.input_context).length > 0 && (
        <JsonSection title="ВХОДНЫЕ ДАННЫЕ" data={execution.input_context} />
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentStudioExecutions() {
  const { system_name, execution_id } = useParams<{
    system_name: string;
    execution_id: string;
  }>();
  const navigate = useNavigate();

  const [execution, setExecution] = useState<ExecutionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!system_name || !execution_id) return;
      try {
        setLoading(true);
        setError(null);
        const result = await api.getAgentExecution(system_name, execution_id);
        if (!cancelled) setExecution(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [system_name, execution_id]);

  if (!system_name || !execution_id) {
    return <ErrorCard message="Не указаны параметры запуска." />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)' }}>
        <Link to="/agents" style={{ color: 'inherit' }}>Студия агентов</Link>
        {' / '}
        <Link to={`/agents/${system_name}`} style={{ color: 'inherit' }}>{system_name}</Link>
        {' / Запуски / '}
        <span style={{ fontFamily: 'monospace' }}>#{execution_id.slice(-8)}</span>
      </div>

      {loading && <LoadingState />}
      {!loading && error && <ErrorCard message={error} />}
      {!loading && !error && execution && (
        <DetailView execution={execution} systemName={system_name} navigate={navigate} />
      )}
    </div>
  );
}
